import { Divider } from '@mui/material';
import { BenefitDTO, BenefitDTOBenefitEnum, CouponBenefitDetailsDTO } from 'probonio-shared-ui/api';
import { BENEFIT_PICTURES } from 'probonio-shared-ui/component/button';
import React from 'react';
import {
  BenefitListItemExtensionInterface,
  BenefitListItemProps,
  getBenefitListItemExtension,
} from '../../../extension/interface/benefit-list-item.extension.interface';
import { getBenefitIcon } from '../../benefit/BenefitIcon';
import { BenefitCardNavButton, BenefitItemCard } from '../../benefit/BenefitItem';
import { NonCashBenefit } from '../../nonCashBenefits/consts/consts';

export class CouponsBenefitListItemExtension extends BenefitListItemExtensionInterface {
  isHidden(activeBenefit: BenefitDTO, activeBenefits: BenefitDTO[], context?: Record<string, unknown>): boolean {
    const detailsCoupons = activeBenefit.details as CouponBenefitDetailsDTO;

    // Hide inactive coupons V1 tile if coupons V2 tile is shown
    return !detailsCoupons.currentlyActivated && !detailsCoupons.nonCashBenefits.length;
  }

  getListItemProps(activeBenefit: BenefitDTO, activeBenefits: BenefitDTO[]): BenefitListItemProps | null {
    const detailsCoupons = activeBenefit.details as CouponBenefitDetailsDTO;

    // If this is used within Flex, provide simple props for Coupons navigation
    const couponBudget = detailsCoupons.monthlyBudget ? detailsCoupons.monthlyBudget - detailsCoupons.usedMonthlyBudget : 0;
    const benefitProgress = detailsCoupons.couponMonthlyBudget
      ? (detailsCoupons.usedMonthlyBudget / detailsCoupons.couponMonthlyBudget) * 100
      : 0;

    return {
      icon: getBenefitIcon(activeBenefit.benefit),
      titleKey: `benefit.${activeBenefit.benefit}.name`,
      navigateTo: '/coupons',
      picture: BENEFIT_PICTURES[activeBenefit.benefit],
      descriptionKey: `benefitModule:benefit.${activeBenefit.benefit}.description.couponsAvailable`,
      descriptionValues: {
        availableBudget: detailsCoupons.coupons.length === 0 || !detailsCoupons.currentlyActivated ? 0 : couponBudget,
        totalAmountLeft: detailsCoupons.totalAmountLeft,
      },
      benefitProgress,
    };
  }

  renderCustomContent(activeBenefit: BenefitDTO, activeBenefits: BenefitDTO[]): React.ReactNode {
    const detailsCoupons = activeBenefit.details as CouponBenefitDetailsDTO;
    const couponBudget = detailsCoupons.monthlyBudget ? detailsCoupons.monthlyBudget - detailsCoupons.usedMonthlyBudget : 0;
    const benefitProgress = detailsCoupons.couponMonthlyBudget
      ? (detailsCoupons.usedMonthlyBudget / detailsCoupons.couponMonthlyBudget) * 100
      : 0;

    const couponProps = {
      icon: getBenefitIcon(activeBenefit.benefit),
      titleKey: `benefit.${activeBenefit.benefit}.name`,
      navigateTo: '/coupons',
      picture: BENEFIT_PICTURES[activeBenefit.benefit],
      descriptionKey: `benefitModule:benefit.${activeBenefit.benefit}.description.couponsAvailable`,
      descriptionValues: {
        availableBudget: detailsCoupons.coupons.length === 0 || !detailsCoupons.currentlyActivated ? 0 : couponBudget,
        totalAmountLeft: detailsCoupons.totalAmountLeft,
      },
      benefitProgress,
    };

    // Complex card rendering for non-flex benefits
    const getNavigationPath = () => {
      if (detailsCoupons.nonCashBenefits.includes(NonCashBenefit.Coupons)) {
        return '/coupons';
      }
      if (detailsCoupons.nonCashBenefits.length > 0) {
        return `/${detailsCoupons.nonCashBenefits[0].toLowerCase().split('_').join('-')}`;
      }
      return '/coupons';
    };

    return (
      <BenefitItemCard
        icon={getBenefitIcon(activeBenefit.benefit)}
        titleKey={`benefit.${activeBenefit.benefit}.alternate.name`}
        navigateTo={getNavigationPath()}
        picture={BENEFIT_PICTURES[activeBenefit.benefit]}
        descriptionKey={`benefitModule:benefit.${activeBenefit.benefit}.alternate.summary`}
        descriptionValues={{ usedBudget: detailsCoupons.usedMonthlyBudget, monthlyBudget: detailsCoupons.couponMonthlyBudget }}
        benefitProgress={benefitProgress}
      >
        {detailsCoupons.nonCashBenefits.includes(BenefitDTOBenefitEnum.Coupons) && (
          <>
            <BenefitCardNavButton {...couponProps} />
            <Divider sx={{ mx: 0.75, borderWidth: 0, borderBottomWidth: 'thin' }} />
          </>
        )}

        {detailsCoupons.nonCashBenefits
          .map(benefit => activeBenefits.find(dto => dto.benefit === benefit))
          .filter((benefitDto): benefitDto is BenefitDTO => !!benefitDto && benefitDto.benefit !== BenefitDTOBenefitEnum.Coupons)
          .map((benefitDto, index, array) => {
            const extension = getBenefitListItemExtension(benefitDto.benefit);
            const listItemProps = extension?.getListItemProps(benefitDto, activeBenefits);

            if (!listItemProps) {
              return null;
            }

            return (
              <React.Fragment key={`${benefitDto.benefit}-${benefitDto.details}`}>
                <BenefitCardNavButton {...listItemProps} />
                {index < array.length - 1 && <Divider sx={{ mx: 0.75, borderWidth: 0, borderBottomWidth: 'thin' }} />}
              </React.Fragment>
            );
          })
          .filter(Boolean)}
      </BenefitItemCard>
    );
  }
}
